/**
 * OpenMeta — Prototype Viewer (Phase 7)
 *
 * Cadre smartphone premium qui affiche un écran actif du prototype et navigue
 * entre écrans via la tab bar en bas. Rendu déterministe des composants issus
 * de la whitelist backend (app_bar, stat_cards, list_items, form_fields,
 * chat_thread, text_block, cta_button, progress_ring, calendar_strip,
 * image_placeholder).
 *
 * Interactions : tap sur tabs, hover, transitions douces. Aucune logique réelle
 * derrière les boutons ou les formulaires — c'est une maquette visuelle.
 */
import { useState } from 'react';
import { getPrototypeIcon } from '@/lib/prototype-icons';

// ────────────────────────────────────────────────────────────────────
// Renderers de composants
// ────────────────────────────────────────────────────────────────────
function AppBar({ comp }) {
  return (
    <div className="pt-1 pb-3">
      <h2 className="text-[17px] font-extrabold leading-tight tracking-tight text-slate-900">
        {comp.title}
      </h2>
      {comp.subtitle && (
        <p className="mt-0.5 text-[11px] text-slate-500">{comp.subtitle}</p>
      )}
    </div>
  );
}

function StatCards({ comp, accent }) {
  return (
    <div className={`grid gap-2 ${comp.items.length >= 3 ? 'grid-cols-2' : 'grid-cols-2'}`}>
      {comp.items.map((it, i) => (
        <div
          key={i}
          className="rounded-2xl border border-slate-200 bg-white p-3"
        >
          <div className="flex items-baseline gap-1.5">
            <div className="text-[18px] font-extrabold tabular-nums text-slate-900">{it.value}</div>
            {it.delta && (
              <div
                className="text-[9px] font-bold uppercase tracking-wider"
                style={{ color: accent }}
              >
                {it.delta}
              </div>
            )}
          </div>
          <div className="mt-0.5 text-[10px] font-medium leading-tight text-slate-500">
            {it.label}
          </div>
        </div>
      ))}
    </div>
  );
}

function ListItems({ comp, accent }) {
  return (
    <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white">
      {comp.items.map((it, i) => {
        const Icon = it.icon ? getPrototypeIcon(it.icon) : null;
        return (
          <div key={i} className="flex items-start gap-2.5 p-2.5 transition-colors hover:bg-slate-50">
            {Icon && (
              <span
                className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full"
                style={{ background: `${accent}18`, color: accent }}
              >
                <Icon size={13} strokeWidth={2.4} />
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12px] font-semibold text-slate-900">{it.title}</div>
              {it.subtitle && (
                <div className="mt-0.5 truncate text-[10px] text-slate-500">{it.subtitle}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FormFields({ comp, accent }) {
  return (
    <div className="space-y-2">
      {comp.items.map((it, i) => {
        const commonCls = 'w-full rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2';
        const focusStyle = { boxShadow: `0 0 0 0 ${accent}` };
        return (
          <div key={i}>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {it.label}
            </label>
            {it.type === 'textarea' ? (
              <textarea
                readOnly
                rows={2}
                placeholder={it.placeholder || ''}
                className={commonCls}
                style={focusStyle}
              />
            ) : it.type === 'select' ? (
              <select disabled className={commonCls} style={focusStyle}>
                <option>{it.placeholder || '—'}</option>
              </select>
            ) : (
              <input
                readOnly
                type={it.type === 'email' ? 'email' : it.type === 'number' ? 'text' : 'text'}
                placeholder={it.placeholder || ''}
                className={commonCls}
                style={focusStyle}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function ChatThread({ comp, accent }) {
  return (
    <div className="space-y-1.5">
      {comp.messages.map((m, i) => {
        const isUser = m.role === 'user';
        return (
          <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-[11px] leading-snug ${
                isUser
                  ? 'text-white'
                  : 'bg-white text-slate-800 border border-slate-200'
              }`}
              style={isUser ? { background: accent } : undefined}
            >
              {m.content}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TextBlock({ comp }) {
  return (
    <p className="text-[11px] leading-relaxed text-slate-700">
      {comp.content}
    </p>
  );
}

function CtaButton({ comp, accent }) {
  return (
    <button
      type="button"
      onClick={(e) => e.preventDefault()}
      className="w-full rounded-full py-2 text-[12px] font-bold text-white transition-transform duration-150 ease-out hover:-translate-y-0.5"
      style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}
    >
      {comp.label}
    </button>
  );
}

function ProgressRing({ comp, accent }) {
  const value = Math.max(0, Math.min(100, comp.value || 0));
  const r = 26;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3">
      <svg width="64" height="64" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={r} fill="none" stroke="#E6E9EF" strokeWidth="5" />
        <circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          stroke={accent}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform="rotate(-90 32 32)"
        />
        <text
          x="32"
          y="36"
          textAnchor="middle"
          fontSize="14"
          fontWeight="800"
          fill="#0A0E1A"
        >
          {value}%
        </text>
      </svg>
      <div className="text-[12px] font-semibold text-slate-800">{comp.label}</div>
    </div>
  );
}

function CalendarStrip({ comp, accent }) {
  return (
    <div className="grid grid-cols-7 gap-1">
      {comp.days.map((d, i) => (
        <div
          key={i}
          className={`flex flex-col items-center rounded-xl border py-1.5 ${
            d.active ? 'border-transparent text-white' : 'border-slate-200 bg-white text-slate-700'
          }`}
          style={d.active ? { background: accent } : undefined}
        >
          <div className="text-[9px] font-semibold uppercase tracking-wider opacity-70">{d.day}</div>
          <div className="mt-0.5 text-[13px] font-extrabold leading-none">{d.label}</div>
        </div>
      ))}
    </div>
  );
}

function ImagePlaceholder({ comp, accent }) {
  return (
    <div
      className="flex h-24 items-center justify-center overflow-hidden rounded-2xl border border-dashed"
      style={{
        borderColor: `${accent}66`,
        background: `linear-gradient(135deg, ${accent}12, ${accent}04)`,
      }}
    >
      <div className="text-center">
        <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: accent }}>
          Image
        </div>
        <div className="mt-0.5 text-[10px] text-slate-500 px-3">{comp.caption}</div>
      </div>
    </div>
  );
}

const RENDERERS = {
  app_bar: AppBar,
  stat_cards: StatCards,
  list_items: ListItems,
  form_fields: FormFields,
  chat_thread: ChatThread,
  text_block: TextBlock,
  cta_button: CtaButton,
  progress_ring: ProgressRing,
  calendar_strip: CalendarStrip,
  image_placeholder: ImagePlaceholder,
};

function ComponentRenderer({ comp, accent }) {
  const R = RENDERERS[comp.type];
  if (!R) return null;
  return <R comp={comp} accent={accent} />;
}

// ────────────────────────────────────────────────────────────────────
// Phone frame + tab bar
// ────────────────────────────────────────────────────────────────────
export function PrototypeViewer({ prototype }) {
  const screens = prototype?.screens || [];
  const [activeId, setActiveId] = useState(screens[0]?.id);
  const active = screens.find((s) => s.id === activeId) || screens[0];
  const accent = prototype?.theme?.accent || '#0A0E1A';

  if (!screens.length) return null;

  return (
    <div className="flex justify-center" data-testid="prototype-viewer">
      <div
        className="relative w-[280px] shrink-0 rounded-[42px] border border-slate-200 bg-[#0A0E1A] p-2 shadow-[0_40px_80px_-30px_rgba(10,14,26,0.5)] sm:w-[300px]"
        style={{ aspectRatio: '9/19' }}
      >
        {/* Encoche */}
        <div className="absolute left-1/2 top-2 z-10 h-4 w-24 -translate-x-1/2 rounded-full bg-[#0A0E1A]" aria-hidden />
        {/* Screen */}
        <div
          className="relative flex h-full flex-col overflow-hidden rounded-[34px] bg-[#FAFBFC]"
          data-testid="prototype-screen"
        >
          <div className="absolute inset-0 flex flex-col">
            {/* Content */}
            <div
              className="flex-1 overflow-y-auto p-4 pt-8"
              data-testid={`prototype-screen-${active?.id}`}
            >
              <div className="space-y-3">
                {(active?.components || []).map((c, i) => (
                  <ComponentRenderer key={`${active.id}-${i}`} comp={c} accent={accent} />
                ))}
              </div>
            </div>
            {/* Tab bar */}
            <nav
              className="border-t border-slate-200 bg-white/90 px-2 py-1.5 backdrop-blur"
              data-testid="prototype-tabbar"
            >
              <div className="flex items-center justify-around">
                {screens.map((s) => {
                  const Icon = getPrototypeIcon(s.icon);
                  const isActive = s.id === active?.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setActiveId(s.id)}
                      className="flex flex-col items-center gap-0.5 rounded-lg px-2 py-1 transition-colors focus:outline-none"
                      data-testid={`prototype-tab-${s.id}`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <span
                        className="grid h-6 w-6 place-items-center rounded-md transition-colors"
                        style={
                          isActive
                            ? { color: accent, background: `${accent}18` }
                            : { color: '#8892A6' }
                        }
                      >
                        <Icon size={13} strokeWidth={2.4} />
                      </span>
                      <span
                        className="text-[9px] font-semibold leading-tight"
                        style={{ color: isActive ? accent : '#8892A6' }}
                      >
                        {s.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrototypeViewer;
